import { useId } from 'react';

/**
 * The base coin asset — a stylized gold medal with an engraved "O"
 * center that mirrors the owl-eye motif in the OllieReads wordmark.
 *
 * Design intent:
 *   - One viewBox (`0 0 64 64`) at every rendered size. The `size` prop
 *     only controls CSS width/height so the coin stays crisp.
 *   - Gradients + filters are inline via `<defs>` so the coin renders
 *     identically regardless of the surrounding CSS stylesheet context
 *     (Tailwind class collision, isolated portals, SSR snapshots).
 *   - `useId()` namespaces the gradient ids so multiple coins on the
 *     same page don't collide on `url(#coin-gold)` references.
 *   - The `inert` prop collapses all decorative text to screen-readers
 *     — use it when the surrounding label already says "coin(s)".
 */
export interface CoinIconProps {
  /** CSS-pixel size. Default 24. */
  size?: number | string;
  /** Render as decorative-only (aria-hidden). Default false. */
  inert?: boolean;
  /** Override the accessible label (default: "coin"). Ignored when `inert`. */
  label?: string;
  className?: string;
}

export function CoinIcon({ size = 24, inert = false, label = 'coin', className }: CoinIconProps) {
  // Namespace gradient ids so a page with multiple coin icons doesn't
  // cross-reference the same `url(#foo)` — Safari in particular renders
  // the first definition everywhere when ids collide.
  const uid = useId().replace(/:/g, '');
  const faceId = `coin-face-${uid}`;
  const rimId = `coin-rim-${uid}`;
  const glossId = `coin-gloss-${uid}`;

  const a11yProps = inert
    ? { 'aria-hidden': true as const, focusable: false }
    : { role: 'img' as const, 'aria-label': label };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...a11yProps}
    >
      <defs>
        {/* Main coin face — warm gold gradient angled so the light
            appears to land top-left. */}
        <linearGradient id={faceId} x1="18%" y1="10%" x2="85%" y2="95%">
          <stop offset="0%" stopColor="#FFE58A" />
          <stop offset="45%" stopColor="#FCC046" />
          <stop offset="100%" stopColor="#E38B1A" />
        </linearGradient>
        {/* Darker rim gradient for depth on the outer ring. */}
        <linearGradient id={rimId} x1="20%" y1="0%" x2="80%" y2="100%">
          <stop offset="0%" stopColor="#B5660F" />
          <stop offset="100%" stopColor="#6F3D05" />
        </linearGradient>
        {/* A soft specular crescent across the top-left of the coin to
            suggest a three-dimensional surface without going full skeuomorphic. */}
        <radialGradient id={glossId} cx="30%" cy="28%" r="45%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.85" />
          <stop offset="60%" stopColor="#FFFFFF" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Outer rim */}
      <circle cx="32" cy="32" r="30" fill={`url(#${rimId})`} />
      {/* Inner face */}
      <circle cx="32" cy="32" r="26" fill={`url(#${faceId})`} />
      {/* Inner ring embossing */}
      <circle
        cx="32"
        cy="32"
        r="22"
        fill="none"
        stroke="#9A5A10"
        strokeOpacity="0.35"
        strokeWidth="1.25"
      />
      {/* Engraved "O" — hollow ring whose stroke darkens toward the
          bottom so the letter reads as pressed-into-metal. Two layers:
          a deeper shadow underlay and the main stroke. */}
      <circle
        cx="32"
        cy="33.25"
        r="10.5"
        fill="none"
        stroke="#6F3D05"
        strokeOpacity="0.35"
        strokeWidth="4"
      />
      <circle cx="32" cy="32" r="10.5" fill="none" stroke="#8C4E0A" strokeWidth="4" />
      {/* Specular highlight — clipped to the face so the gloss doesn't
          leak onto the rim. */}
      <circle cx="32" cy="32" r="26" fill={`url(#${glossId})`} />
    </svg>
  );
}
